// typescript
// File: `typescript/apps/front-end/src/main/common/GenerationUtils.ts`

import { getUserOwnedImageReferenceFromUrl, getUriForUserOwnedImage, MEDIUM_IMAGE_SIZE } from "@/main/util/ImageUrlHelper";


const collectImagesFromTipTapJson = (node: any, out: string[] = []) => {
    if (!node) return out;
    if (Array.isArray(node)) {
        for (const n of node) collectImagesFromTipTapJson(n, out);
        return out;
    }
    if (node.type === "image" && node.attrs && node.attrs.src) {
        out.push(node.attrs.src);
    }
    if (node.type === "linkCard" && node.attrs?.imageUrl) {
        out.push(node.attrs.imageUrl);
    }
    if (node.content) {
        collectImagesFromTipTapJson(node.content, out);
    }
    return out;
};

const collectTextFromTipTapJson = (node: any): string => {
    if (!node) return "";
    if (Array.isArray(node)) return node.map(n => collectTextFromTipTapJson(n)).join(" ");
    if (node.type === "text") return node.text || "";
    if (node.type === "linkCard") {
        return [
            node.attrs?.siteName,
            node.attrs?.title,
            node.attrs?.description,
            node.attrs?.displayUrl,
        ].filter(Boolean).join(" ");
    }
    if (node.text) return node.text;
    if (node.content) return node.content.map((c: any) => collectTextFromTipTapJson(c)).join(" ");
    return "";
};

const normalizeCollectedText = (text: string): string => text.replace(/\s+/g, " ").trim();

const getTipTapNodeText = (node: any): string => {
    if (!node) return "";
    return normalizeCollectedText(collectTextFromTipTapJson(node.content ?? node));
};

const findFirstMatchingTipTapText = (node: any, predicate: (candidate: any) => boolean): string | null => {
    if (!node) return null;

    if (Array.isArray(node)) {
        for (const child of node) {
            const found = findFirstMatchingTipTapText(child, predicate);
            if (found) {
                return found;
            }
        }
        return null;
    }

    if (predicate(node)) {
        const text = getTipTapNodeText(node);
        if (text) {
            return text;
        }
    }

    if (node.content) {
        return findFirstMatchingTipTapText(node.content, predicate);
    }

    return null;
};

const collectMatchingTipTapTexts = (
    node: any,
    predicate: (candidate: any) => boolean,
    limit = Number.POSITIVE_INFINITY,
    out: string[] = [],
): string[] => {
    if (!node || out.length >= limit) {
        return out;
    }

    if (Array.isArray(node)) {
        for (const child of node) {
            collectMatchingTipTapTexts(child, predicate, limit, out);
            if (out.length >= limit) {
                break;
            }
        }
        return out;
    }

    if (predicate(node)) {
        const text = getTipTapNodeText(node);
        if (text) {
            out.push(text);
        }
    }

    if (node.content && out.length < limit) {
        collectMatchingTipTapTexts(node.content, predicate, limit, out);
    }

    return out;
};

const EXCERPT_ELLIPSIS = "…";
const SHORT_FIRST_PARAGRAPH_CHAR_THRESHOLD = 300;
const MULTI_PARAGRAPH_EXCERPT_TARGET_CHARS = 400;
const MAX_SHORT_EXCERPT_PARAGRAPHS = 3;

const EXCERPT_BODY_FALLBACK_NODE_TYPES = new Set([
    "blockquote",
    "codeBlock",
    "bulletList",
    "orderedList",
    "listItem",
    "table",
    "tableRow",
    "tableCell",
    "tableHeader",
]);

export const extractPlainTextFromContent = (content: string): string => {
    if (!content) return "";

    try {
        const parsed = JSON.parse(content);
        if (parsed && (parsed.type === "doc" || parsed.content)) {
            return normalizeCollectedText(collectTextFromTipTapJson(parsed.content ?? parsed));
        }
    } catch {
        // ignore
    }

    return "";
};

export const getPlainTextLengthFromContent = (content: string): number => {
    return extractPlainTextFromContent(content).length;
};

export const normalizeImageUrl = (url: string) => {
    if (!url) return url;
    if (/^https?:\/\//.test(url)) return url;
    const imageReference = getUserOwnedImageReferenceFromUrl(url);
    if (imageReference) {
        return getUriForUserOwnedImage(imageReference, MEDIUM_IMAGE_SIZE);
    }
    return url;
};

export const extractImagesFromContent = (content: string): string[] => {
    if (!content) return [];
    // TipTap JSON
    try {
        const parsed = JSON.parse(content);
        if (parsed && (parsed.type === "doc" || parsed.content)) {
            const imgs = collectImagesFromTipTapJson(parsed.content ?? parsed);
            const seen = new Set<string>();
            return imgs.filter(src => {
                if (!src) return false;
                if (seen.has(src)) return false;
                seen.add(src);
                return true;
            });
        }
    } catch {
        // not JSON
    }

    return [];
};

export const extractTopTagsFromContent = (content: string, limit = 20, minLength = 5): string[] => {
    let text = "";

    try {
        const parsed = JSON.parse(content);
        if (parsed && (parsed.type === "doc" || parsed.content)) {
            text = extractPlainTextFromContent(content).toLowerCase();
        }
    } catch {
        // ignore
    }




    const rawWords = text.split(/\s+/).map(w => w.replace(/^[^a-z]+|[^a-z]+$/g, ""));
    const filtered = rawWords.filter(w => new RegExp(`^[a-z]{${Math.max(minLength, 1)},}$`).test(w));

    const freq = new Map<string, number>();
    for (const w of filtered) {
        freq.set(w, (freq.get(w) || 0) + 1);
    }

    const sorted = Array.from(freq.entries())
        .sort((a, b) => b[1] - a[1])
        .map(([word]) => word);

    return sorted.slice(0, limit);
};

export const generateExcerptFromContent = (content: string, maxWords = 500, maxChars?: number): string => {
    const trimToWords = (text: string, maxW: number) => {
        const words = text.replace(/\s+/g, " ").trim().split(" ").filter(Boolean);
        if (words.length <= maxW) return words.join(" ");
        return words.slice(0, maxW).join(" ") + EXCERPT_ELLIPSIS;
    };

    const cutAtWordBoundaryWithEllipses = (text: string, limit: number) => {
        if (!limit || text.length <= limit) return text;
        if (limit <= EXCERPT_ELLIPSIS.length) {
            return EXCERPT_ELLIPSIS.slice(0, limit);
        }
        const slice = text.slice(0, limit - EXCERPT_ELLIPSIS.length);
        const lastSpace = slice.lastIndexOf(" ");
        if (lastSpace > 0) {
            return slice.slice(0, lastSpace).trim() + EXCERPT_ELLIPSIS;
        }
        return slice.trim() + EXCERPT_ELLIPSIS;
    };

    const buildPreferredParagraphExcerpt = (contentRoot: any): { text: string; usedMultipleParagraphs: boolean } | null => {
        const paragraphs = collectMatchingTipTapTexts(
            contentRoot,
            (node) => node?.type === "paragraph",
            MAX_SHORT_EXCERPT_PARAGRAPHS,
        );
        const firstParagraph = paragraphs[0];
        if (!firstParagraph) {
            return null;
        }

        const canExpandShortExcerpt = typeof maxChars !== "number"
            || maxChars >= MULTI_PARAGRAPH_EXCERPT_TARGET_CHARS;

        if (!canExpandShortExcerpt || firstParagraph.length >= SHORT_FIRST_PARAGRAPH_CHAR_THRESHOLD) {
            return {
                text: firstParagraph,
                usedMultipleParagraphs: false,
            };
        }

        let combinedExcerpt = firstParagraph;
        for (const paragraph of paragraphs.slice(1)) {
            if (combinedExcerpt.length >= MULTI_PARAGRAPH_EXCERPT_TARGET_CHARS) {
                break;
            }
            combinedExcerpt = normalizeCollectedText(`${combinedExcerpt} ${paragraph}`);
        }

        return {
            text: combinedExcerpt,
            usedMultipleParagraphs: combinedExcerpt !== firstParagraph,
        };
    };

    const finalize = (txt: string | null) => {
        if (!txt) return "";
        const byWords = trimToWords(txt, maxWords);
        if (typeof maxChars === "number") return cutAtWordBoundaryWithEllipses(byWords, maxChars);
        return byWords;
    };

    if (!content) return "";

    // TipTap JSON
    try {
        const parsed = JSON.parse(content);
        if (parsed && (parsed.type === "doc" || parsed.content)) {
            const contentRoot = parsed.content ?? parsed;
            const preferredParagraphExcerpt = buildPreferredParagraphExcerpt(contentRoot);
            if (preferredParagraphExcerpt?.text) {
                const {text, usedMultipleParagraphs} = preferredParagraphExcerpt;
                // if first paragraph is longer than 200 chars, stop there and use that paragraph only (truncated at word boundary)
                if (!usedMultipleParagraphs && text.length > 200) {
                    return cutAtWordBoundaryWithEllipses(text, typeof maxChars === "number" ? maxChars : 200);
                }
                return finalize(text);
            }

            const firstBodyFallback = findFirstMatchingTipTapText(
                contentRoot,
                (node) => EXCERPT_BODY_FALLBACK_NODE_TYPES.has(node?.type),
            );
            if (firstBodyFallback) {
                return finalize(firstBodyFallback);
            }

            const firstLinkCard = findFirstMatchingTipTapText(contentRoot, (node) => node?.type === "linkCard");
            if (firstLinkCard) {
                return finalize(firstLinkCard);
            }

            const fallbackText = extractPlainTextFromContent(content);
            if (fallbackText) {
                return finalize(fallbackText);
            }
        }
    } catch {
        // not JSON
    }


    return "";
};

export const findCandidateIndex = (candidates: string[], imageUrl?: string): number => {
    if (!imageUrl) return -1;
    const norm = normalizeImageUrl(imageUrl);
    return candidates.findIndex(c => normalizeImageUrl(c) === norm);
};

export const nextImageSelection = (candidates: string[], currentIndex = 0): { nextIndex: number, nextUrl: string | null } => {
    if (!Array.isArray(candidates) || candidates.length === 0) {
        return { nextIndex: 0, nextUrl: null };
    }
    const nextIndex = (currentIndex + 1) % candidates.length;
    return { nextIndex, nextUrl: normalizeImageUrl(candidates[nextIndex]) };
};
